import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { head, isEmpty } from "lodash";

import CartItems from "../containers/CartItems";

import CartDetails from "../containers/CartDetails";
import Button from "./Button";
import TabOrderCartDetails from "../components/tabs/TabOrderCartDetails";

import styles from "./CartSidebar.module.scss";
import networkConnected from "../utils/networkConnected";
import ConfirmSplitPaymentDialog from "./dialog/ConfirmSplitPaymentDialog";
import useOfflineModal from "../hooks/useOfflineModal";

import { MODES } from "../VNMode/Reducer";
import { useDispatch, useSelector } from "react-redux";
import { getDeviceMode } from "../VNMode/Selectors";
import { getPaymentProgress } from "../VNCustomerFacingDisplay/Selectors";
import { addTipOrderInProgress } from "../actions/order";
import { CFD_SCREEN } from "../VNCustomerFacingDisplay/Reducer";
import useCreateOrderInProgress from "../hooks/useCreateOrderInProgress";
import { sendCFDScreenNav } from "../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend";
import { setCurrentPaymentFlow, setUserProgress } from "../VNCustomerFacingDisplay/ActionCreators";
import { CFD_POS_PAYMENT_FLOWS } from "../VNCustomerFacingDisplay/Enums";
import { setConfirmPaymentDialog, setOpenCustomerPaymentDialog } from "../VNDialogs/ActionCreators";
import { getCustomerPaymentDialogOpen } from "../VNDialogs/Selectors";
import { getOrderInProgress } from "../selectors/order";

const CartSidebar = ({
  topButton,
  bottomButton,
  clearCart,
  hasCartItems,
  order,
  history,
  discountsAvailable,
  noOpTenders,
  className,
  setMenus,
  stands,
  menu,
  cashEnabled,
  ticketEnabled,
  tipEnabled,
  mobilePaySetting,
  clearPromotionsOnOrder,
  giftCardEnabled,
  startCFDPayment,
  requiredOrderData
}) => {
  const classNames = cn(styles.cartSidebar, className);
  const [showSplitPayDialog, setShowSplitPayDialog] = useState(false);
  const [checkIfOfflineWithCallback, offlineModal] = useOfflineModal()
  const [createOrder, orderCreated] = useCreateOrderInProgress()
  const [cartTotals, setCartTotals] = useState(null)

  const cfdMode = useSelector(state => getDeviceMode(state))
  const orderInProgress = useSelector(state => getOrderInProgress(state))
  const paymentProgress = useSelector(state => getPaymentProgress(state))
  const qrPayDialog = useSelector(state => getCustomerPaymentDialogOpen(state))

  const dispatch = useDispatch()

  const clearItemsAndPromotions = () => {
    clearCart()
    clearPromotionsOnOrder()
  }

  const isTab = !isEmpty(order);
  let modal = null;
  let cartFooterElement = null;
  const offlineModalScanScreenMessage = 'This payment tender is not supported\nwhile the device is offline'

  useEffect(() => {
    if(paymentProgress?.tipAmount && qrPayDialog?.get('open')) {
      createOrder()
    }
  }, [paymentProgress, qrPayDialog])

  useEffect(() => {
    const tipAmount = parseInt(paymentProgress?.tipAmount)
    if(paymentProgress?.tipAmount && qrPayDialog?.get('open') && orderCreated) {
      dispatch(setOpenCustomerPaymentDialog({open: false}))
      dispatch(addTipOrderInProgress(tipAmount))
      checkIfOfflineWithCallback(() => history.push('/qr-pay'), offlineModalScanScreenMessage, true)
      sendCFDScreenNav(CFD_SCREEN.PRESENT_QR_PAY_CODE, {cartTotals: {...cartTotals, uuid: orderInProgress.uuid}})
    }
  }, [paymentProgress, qrPayDialog, orderCreated])

  useEffect(() => {
    // if offline
    networkConnected().catch(() => {
      if (menu) setMenus(head(stands), menu);
    });
  }, [menu]);

  if (showSplitPayDialog) {
    modal = (
      <ConfirmSplitPaymentDialog
        onClose={() => setShowSplitPayDialog(false)}
        fromConcessions={true}
      />
    );
  }

  const handleScanScreenSelection = (scanScreenPath) => {
    if (tipEnabled) {
      history.push({
        pathname: '/signature',
        state: {
          addTipThenRedirectPath: scanScreenPath,
        }
      })
      return
    }
    history.push(scanScreenPath)
  }

  const trueButtonCount =
    !!cashEnabled +
    !isEmpty(noOpTenders) +
    !!mobilePaySetting +
    !!ticketEnabled + 1

  let buttonCount;
  if (trueButtonCount > 4) {
    buttonCount = 4;
  } else {
    buttonCount = trueButtonCount;
  }

  const oddButtonCount = buttonCount % 2 === 1;

  cn([styles.checkout, buttonCount === 1 ? styles.long : styles.short])
  const showOtherButton = trueButtonCount > 3;

  let buttons = [
    <Button
      className={cn([styles.checkout, buttonCount === 1 ? styles.long : styles.short])}
      disabled={topButton.disabled}
      onClick={topButton.onClick}
    >
      {topButton.text}
    </Button>,
  ];

  if (cashEnabled) {
    buttons.push(
      <Button className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])} onClick={bottomButton.onClick}>
        {bottomButton.text}
      </Button>
    );
  }

  if (
    (buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) &&
    mobilePaySetting
  ) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => {

          // CFD - Rich Checkout Button
          if (cfdMode === MODES.POS && mobilePaySetting === "pos_shows_qr") {
            startCFDPayment(true)
            return
          }
          if (mobilePaySetting === "pos_shows_qr") {
            history.push("/rich-checkout");
            return;
          }

          // qr pay for cfd
          if (cfdMode === MODES.POS) {
            checkIfOfflineWithCallback(() => {
              dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.QR_PAY, true))
              const calculatedCartTotals = requiredOrderData.getCalculatedCartTotals()
              dispatch(setOpenCustomerPaymentDialog({open: true, tipsEnabled: tipEnabled, amount: calculatedCartTotals?.total}))
              setCartTotals(calculatedCartTotals)
              dispatch(setUserProgress({})) // reset the user progress incase of a tip already existing
              sendCFDScreenNav(CFD_SCREEN.CARD_PAYMENT, { cartTotals: calculatedCartTotals, executeSalesRequest: false, tips: { enabled: tipEnabled, suggestions: requiredOrderData.tipSuggestions }})
            }, offlineModalScanScreenMessage, true)
          } else {
            checkIfOfflineWithCallback(() => handleScanScreenSelection('/qr-pay'), offlineModalScanScreenMessage, true)
          }
        }}
      >
        {mobilePaySetting === "pos_shows_qr" ? "App" : "QR Pay"}
      </Button>
    );
  }

  if (
    (buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) &&
    ticketEnabled && cfdMode !== MODES.POS
  ) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => checkIfOfflineWithCallback(() => handleScanScreenSelection('/ticket'), offlineModalScanScreenMessage, true)}
      >
        Ticket
      </Button>
    );
  }

  if (
    (buttons.length < 3 || (buttons.length < 4 && !showOtherButton)) &&
    giftCardEnabled
  ) {
    buttons.push(
      <Button // ((buttons.length < 4 && !isEmpty(noOpTenders)) ? 1 : 0) looks forward one if statement and updates the button length
        // so the correct styling is applied
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1+((buttons.length < 4 && !isEmpty(noOpTenders)) ? 1 : 0) ? styles.long : styles.short])}
        onClick={() => checkIfOfflineWithCallback(() => handleScanScreenSelection('/gift-card'), offlineModalScanScreenMessage, true)}
      >
        Gift Card
      </Button>
    );
  }

  if (showOtherButton || (buttons.length < 4 && !isEmpty(noOpTenders))) {
    buttons.push(
      <Button
        className={cn([styles.checkout, oddButtonCount && buttonCount === buttons.length+1 ? styles.long : styles.short])}
        onClick={() => history.push("/tender-selection")}
      >
        Other
      </Button>
    );
  }
  
  buttons.push(
    <Button
      className={cn([styles.checkout, styles.long])}
      onClick={() => checkIfOfflineWithCallback(() => dispatch(setConfirmPaymentDialog(true)), 'Sorry, this device is now offline. Please\nchoose another method of payment.', true)}
    >
      Split
    </Button>
  )

  if (hasCartItems) {
    cartFooterElement = (
      <div className={styles.cartDetailsContainer}>
        <CartDetails
          className={styles.cartDetails}
          order={order}
          lineItemPrefix={isTab ? "new" : ""}
        />
        <div className={styles.buttonContainer}>{buttons}</div>
      </div>
    );
  }

  return (
    <div className={classNames}>
      
      {hasCartItems && (
        <div className={styles.clearContainer}>
          <span className={styles.headerText}>New Order</span>
          <span className={styles.clear} onClick={clearItemsAndPromotions}>
            Clear
          </span>
        </div>
      )}
      <div className={styles.border}></div>
      {isTab && <TabOrderCartDetails order={order} />}
      <CartItems
        className={styles.cartItems}
        discountsAvailable={discountsAvailable}
      />
      {cartFooterElement}
      {modal}
      {offlineModal}
    </div>
  );
};

CartSidebar.defaultProps = {
  clearCart: () => {},
  order: {},
};

CartSidebar.propTypes = {
  clearCart: PropTypes.func,
  hasCartItems: PropTypes.bool,
  className: PropTypes.string,
  order: PropTypes.object,
};

export default CartSidebar;
